import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid2';

import { AppButton } from '@/components/atoms';
import { useVehicleSearchForm } from '@/hooks/useVehicleSearchForm';
import { type VehicleSearchFormInput, type VehicleSearchFormValues } from '@/schemas/vehicleSearch';
import { darkGlassFormSx } from '@/theme';

import {
  allSearchFields,
  type FieldLayout,
  primarySearchFields,
  secondarySearchFields,
} from './searchFields';
import { VehicleSearchFields } from './VehicleSearchFields';

/**
 * `wide`: a row of fields, all eight at once (/cars on desktop).
 * `stacked`: one column, all eight (the /cars filter drawer).
 * `hero`: make, model, budget and the button, with the other five behind
 * "More filters" — a hero that asks for eight answers gets none (§16).
 */
export type SearchFormLayout = 'wide' | 'stacked' | 'hero';

/**
 * What the form is standing on. `dark-glass` repaints the fields for a smoked
 * glass panel; it is opt-in because the same form also renders on an ordinary
 * page and inside the filter drawer, where the theme's own field styling is
 * already right.
 */
export type SearchFormSurface = 'plain' | 'dark-glass';

type Props = {
  defaultValues: VehicleSearchFormInput;
  onSubmit: (values: VehicleSearchFormValues) => void;
  layout?: SearchFormLayout;
  submitLabel?: string;
  surface?: SearchFormSurface;
};

const fieldLayout = (layout: SearchFormLayout): FieldLayout =>
  layout === 'stacked' ? 'column' : 'row';

/** §16: the one vehicle search form, used on the home page and on /cars. */
export function VehicleSearchForm({
  defaultValues,
  onSubmit,
  layout = 'wide',
  submitLabel = 'Search Cars',
  surface = 'plain',
}: Props) {
  const hero = layout === 'hero';
  const form = useVehicleSearchForm({
    defaultValues,
    onSubmit,
    advancedOpenByDefault: !hero,
  });

  const cells = fieldLayout(layout);
  const submitSize = layout === 'stacked' ? { xs: 12 } : { xs: 12, sm: 6, md: 3 };

  return (
    <Box
      component="form"
      role="search"
      aria-label="Search for your car"
      noValidate
      onSubmit={form.submit}
      sx={surface === 'dark-glass' ? darkGlassFormSx : {}}
    >
      <Grid container spacing={4} alignItems="flex-start">
        <VehicleSearchFields
          form={form}
          fields={hero ? primarySearchFields : allSearchFields}
          layout={cells}
        />
        <Grid size={submitSize} sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <AppButton
            type="submit"
            size="large"
            fullWidth
            startIcon={<SearchIcon />}
            sx={{ minHeight: 56 }}
          >
            {submitLabel}
          </AppButton>
        </Grid>
      </Grid>

      {hero && (
        <>
          <Collapse in={form.advancedOpen} id={form.advancedPanelId}>
            <Grid container spacing={4} sx={{ pt: 4 }}>
              <VehicleSearchFields form={form} fields={secondarySearchFields} layout={cells} />
            </Grid>
          </Collapse>
          <AppButton
            appVariant="ghost"
            onClick={form.toggleAdvanced}
            aria-expanded={form.advancedOpen}
            aria-controls={form.advancedPanelId}
            endIcon={
              <ExpandMore
                sx={{
                  transform: form.advancedOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 200ms ease',
                }}
              />
            }
            sx={{ mt: 3, ml: -3 }}
          >
            {form.advancedOpen ? 'Fewer filters' : 'More filters'}
          </AppButton>
        </>
      )}
    </Box>
  );
}
